// @ts-strict-ignore
import {COMMA, ENTER} from "@angular/cdk/keycodes";
import {Component, Injector} from "@angular/core";
import {FormArray, FormControl, FormGroup} from "@angular/forms";
import {EventState, Scenario, ScenarioGroupView, SimUser, TrainingRoom, TrainingRoomGroupView} from "@models";
import {SimScenarioService, SimTrainingRoomService, SimUserService, SimUtilityService} from "@services";
import {Translations} from "@translations";
import {forkJoin} from "rxjs";
import {
	BasicMaterialDialog,
	MatDialogDataBaseInput,
	MatDialogDataBaseOutput,
} from "../../basic-material-dialog/basic-material-dialog.directive";

export interface DialogData_EventFilterDialogInput extends MatDialogDataBaseInput {
	selectedEventId?: string;
	appliedFilter?: EVENT_FILTER_OPTIONS;
}

export interface EVENT_FILTER_OPTIONS {
	selectedOwnerId?: string;
	selectedScenarioId?: string;
	selectedParticipantIds?: string[];
	selectedRoomIds?: string[];
	selectedEventStatus?: EventState[];
	selectedDateRange?: {
		start: Date | string;
		end: Date | string;
	};
	selectedShowFavoritesOnly?: boolean;
}

export interface DialogData_EventFilterDialogOutput extends MatDialogDataBaseOutput {
	appliedFilter?: EVENT_FILTER_OPTIONS;
}

@Component({
	selector: "sim-event-filter-dialog",
	templateUrl: "./event-filter-dialog.component.html",
	styleUrls: ["./event-filter-dialog.component.scss"],
	standalone: false,
})
export class EventFilterDialogComponent extends BasicMaterialDialog<
	EventFilterDialogComponent,
	DialogData_EventFilterDialogInput,
	DialogData_EventFilterDialogOutput
> {
	T = Translations;

	// Formgroup for Filter
	filterForm = new FormGroup({
		ownerFilter: new FormArray<FormControl<SimUser>>([]),
		scenarioFilter: new FormArray<FormControl<Scenario>>([]),
		participantsFilter: new FormArray<FormControl<SimUser>>([]),
		eventStatusFilter: new FormControl<number[]>([]),
		dateFilter: new FormGroup({
			start: new FormControl<Date>(null),
			end: new FormControl<Date>(null),
		}),
		roomFilter: new FormArray<FormControl<TrainingRoom>>([]),
		favoritesFilter: new FormControl(false),
	});

	/*
	 * MatAutocomplete chiplist attributes
	 */
	visible = true;
	selectable = true;
	removable = true;
	addOnBlur = false;
	separatorKeysCodes: number[] = [ENTER, COMMA];

	eventStati: number[] = [];

	// Filter lists
	public users: SimUser[] = null;
	public scenarios: ScenarioGroupView[];
	public rooms: TrainingRoomGroupView[];

	constructor(
		_injector: Injector,
		public _simUserService: SimUserService,
		public _scenarioService: SimScenarioService,
		public _trainingRoomService: SimTrainingRoomService,
	) {
		super(_injector);

		// Get all possible eventStati from enum
		for (const key in EventState) {
			if (!isNaN(Number(key))) {
				this.eventStati.push(parseInt(key));
			}
		}

		// Set preapplied selected events
		if (this.data.appliedFilter?.selectedEventStatus) {
			this.filterForm.controls.eventStatusFilter.setValue(
				this.data.appliedFilter?.selectedEventStatus.map((status) => status.valueOf()),
			);
		}

		// Set preapplied selected daterange
		if (this.data.appliedFilter?.selectedDateRange) {
			this.filterForm.controls.dateFilter.controls.start.setValue(new Date(this.data.appliedFilter.selectedDateRange?.start));
			this.filterForm.controls.dateFilter.controls.end.setValue(new Date(this.data.appliedFilter.selectedDateRange?.end));
		}

		this.filterForm.controls.favoritesFilter.setValue(!!this.data.appliedFilter?.selectedShowFavoritesOnly);

		forkJoin([
			this._simUserService.listUsers(),
			this._scenarioService.getScenariosGrouped(),
			this._trainingRoomService.getTrainingRoomsByIds(this.data.appliedFilter?.selectedRoomIds ?? []),
		]).subscribe(([simUsers, scenarios, trainingRooms]) => {
			// After users are loaded
			this.users = simUsers.data;

			// Apply preselected owner
			if (this.data.appliedFilter?.selectedOwnerId) {
				const userToBeSet = this.users.find((user: SimUser) => user.id === this.data.appliedFilter?.selectedOwnerId);

				if (userToBeSet) {
					this.filterForm.controls.ownerFilter.push(new FormControl(userToBeSet));
				}
			}

			// Apply preselected participants
			if (this.data.appliedFilter?.selectedParticipantIds) {
				for (const selectedParticipantId of this.data.appliedFilter?.selectedParticipantIds) {
					const foundUser = this.users.find((user: SimUser) => user.id === selectedParticipantId);
					if (foundUser) {
						this.filterForm.controls.participantsFilter.push(new FormControl(foundUser));
					}
				}
			}

			// Apply preselected training rooms
			for (const room of trainingRooms) {
				this.filterForm.controls.roomFilter.push(new FormControl(room));
			}

			// After scenarios are loaded
			this.scenarios = scenarios;

			// Apply possible preselected scenario
			if (this.data.appliedFilter?.selectedScenarioId) {
				const flatScenarios = this.scenarios.flatMap((group) => group.scenarios);
				const foundScenario = flatScenarios.find((scenario) => scenario.id === this.data.appliedFilter?.selectedScenarioId);
				if (foundScenario) {
					this.filterForm.controls.scenarioFilter.push(new FormControl(foundScenario));
				}
			}
		});
	}

	/**
	 * Apply Filter
	 * closes the dialog and returns the selected filters
	 */
	applyFilter() {
		const selectedOwnerId = this.filterForm.controls.ownerFilter.value[0]?.id;
		const selectedScenarioId = this.filterForm.controls.scenarioFilter.value[0]?.id;
		const selectedParticipantIds = this.filterForm.controls.participantsFilter.value.map((participant) => participant.id);
		const selectedRoomIds = this.filterForm.controls.roomFilter.value.map((room) => room.id);

		let selectedEventStati: EventState[] = this.filterForm.controls.eventStatusFilter.value ?? [];

		const start = SimUtilityService.getStartofDay(this.filterForm.controls.dateFilter.value.start)?.toDate();
		const end = SimUtilityService.getEndofDay(this.filterForm.controls.dateFilter.value.end)?.toDate();

		const selectedDateRange: {start: Date; end: Date} = {
			start: start,
			end: end,
		};

		const showFavoritesOnly = this.filterForm.value.favoritesFilter;

		this.close({
			action: "ok",
			appliedFilter: {
				selectedOwnerId: selectedOwnerId,
				selectedScenarioId: selectedScenarioId,
				selectedParticipantIds: selectedParticipantIds,
				selectedRoomIds: selectedRoomIds,
				selectedEventStatus: selectedEventStati,
				selectedDateRange: selectedDateRange,
				selectedShowFavoritesOnly: showFavoritesOnly,
			},
		});
	}

	/**
	 * Used to display scenario as (displayname)
	 * @param scenario scenario to be displayed
	 */
	getScenarioName(scenario: Scenario) {
		return scenario.name;
	}

	/*
	 * Methods used for Owner Filter
	 */

	clearOwnerValue() {
		this.filterForm.controls.ownerFilter.clear();
	}

	/*
	 * Methods used for Participants Filter
	 */

	/**
	 * Clear participant search string and list entries
	 */
	clearPartValues() {
		this.filterForm.controls.participantsFilter.clear();
	}

	/*
	 * Methods used for Scenario Filter
	 */

	clearScenarioValue() {
		this.filterForm.controls.scenarioFilter.clear();
	}

	/*
	 * Methods for TrainingRoom Filter
	 */

	/**
	 * Clear room search string and all list entries
	 */
	clearRoomValues() {
		this.filterForm.controls.roomFilter.clear();
	}
}
